import React, { useState } from 'react';
import PropTypes from 'prop-types';
import jsPDF from 'jspdf';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import { useAuthProvider } from '../../providers/AuthProvider';
import { useAlert } from '../../providers/AlertProvider';
import logger from "../../utils/logger";


/**
 * QRViewModal.jsx
 * 
 * Reusable modal for displaying QR codes in a clean, centered view
 */
export default function QRViewModal({ 
    qrUrl, 
    title, 
    productId, 
    productDescription, 
    palletData = null,  // New prop for complete pallet data
    productData = null, // New prop for complete product data  
    onRegenerateQR = null, // Callback for QR regeneration
    onClose 
}) {
    const { isDev } = useAuthProvider();
    const { showAlert } = useAlert();
    const [isRegenerating, setIsRegenerating] = useState(false);
    

    
    if (!qrUrl) return null;

    // Function to download the QR code as a PDF - only allow for pallets
    const downloadable = palletData ? true : false;

    // Function to regenerate QR code with confirmation
    const handleRegenerateQR = () => {
        if (!onRegenerateQR || isRegenerating) return;
        
        showAlert({
            message: 'Are you sure you want to regenerate this QR code? The current QR code will be replaced with a new one containing updated data format.',
            type: 'warning',
            buttons: [
                {
                    label: 'Cancel',
                    style: 'secondary'
                },
                {
                    label: 'Regenerate',
                    style: 'danger',
                    onClick: async () => {
                        setIsRegenerating(true);
                        try {
                            // Determine item ID and table based on available data
                            let itemId, table;
                            
                            if (palletData && palletData.id) {
                                itemId = palletData.id;
                                table = 'pallets';
                            } else if (productData && productData.id) {
                                itemId = productData.id;
                                table = 'products';
                            } else {
                                logger.error('No valid item data found for QR regeneration');
                                return;
                            }
                            
                            await onRegenerateQR(itemId, table);
                        } catch (error) {
                            logger.error('Error regenerating QR code:', error);
                        } finally {
                            setIsRegenerating(false);
                        }
                    }
                }
            ]
        });
    };

    const downloadPDF = async () => {
        try {
            // Create a new jsPDF instance
            const pdf = new jsPDF();
            
            // Load the QR code image
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            img.onload = function() {
                // Calculate content dimensions and positioning
                const pageWidth = 210; // A4 width in mm
                const pageHeight = 297; // A4 height in mm
                
                // Content box dimensions - made larger to fill more of the page
                const boxWidth = 170;
                const boxHeight = 220;
                const boxX = (pageWidth - boxWidth) / 2;
                const boxY = (pageHeight - boxHeight) / 2;
                
                // Draw outer box around all content with rounded corners
                pdf.setDrawColor(0, 0, 0); // Black border
                pdf.setLineWidth(1);
                pdf.roundedRect(boxX, boxY, boxWidth, boxHeight, 5, 5); // Rounded corners
                
                // Calculate positions relative to the box
                const contentStartY = boxY + 25;
                
                // Add title
                pdf.setFontSize(18); // Slightly larger title
                pdf.setFont(undefined, 'bold');
                pdf.setTextColor(0, 0, 0);
                pdf.text(title || 'QR Code', pageWidth / 2, contentStartY, { align: 'center' });
                
                // QR code positioning (centered in box) - made larger
                const imgWidth = 100;
                const imgHeight = 100;
                const imgX = (pageWidth - imgWidth) / 2;
                const imgY = contentStartY + 20;
                
                // Add border around QR code (matching viewer style)
                pdf.setDrawColor(221, 221, 221); // #ddd color
                pdf.setLineWidth(0.5);
                pdf.roundedRect(imgX - 2, imgY - 2, imgWidth + 4, imgHeight + 4, 2, 2);
                
                pdf.addImage(img, 'PNG', imgX, imgY, imgWidth, imgHeight);
                
                // Add instructions text (matching viewer style) - slightly larger
                pdf.setFontSize(10);
                pdf.setFont(undefined, 'normal');
                pdf.setTextColor(102, 102, 102); // #666 color
                pdf.text('Point your camera at the QR code to scan', pageWidth / 2, imgY + imgHeight + 18, { align: 'center' });
                
                // Reset text color to black for product information
                pdf.setTextColor(0, 0, 0);
                                
                // Save the PDF
                const filename = `QR_Code_${productId || 'Lane'}_${new Date().getTime()}.pdf`;
                pdf.save(filename);
            };
            
            img.onerror = function() {
                logger.error('Failed to load QR code image for PDF');
                alert('Failed to generate PDF. Please try again.');
            };
            
            img.src = qrUrl;
            
        } catch (error) {
            logger.error('Error generating PDF:', error);
            alert('Failed to generate PDF. Please try again.');
        }
    };

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2000, // Higher than LaneEditor's 1000
            }}
            onClick={onClose} // Close when clicking backdrop
        >
            <div
                style={{
                    backgroundColor: "white",
                    padding: "2rem",
                    borderRadius: "12px",
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
                    textAlign: "center",
                    maxWidth: "400px",
                    width: "90%",
                    position: "relative",
                }}
                onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
            >
                {/* Dev QR Regenerate button */}
                {isDev && onRegenerateQR && (
                    <button
                        onClick={handleRegenerateQR}
                        disabled={isRegenerating}
                        style={{
                            position: "absolute",
                            top: "10px",
                            left: "10px",
                            background: isRegenerating ? "#6c757d" : "#ffc107",
                            border: "none",
                            borderRadius: "4px",
                            color: "#000",
                            cursor: isRegenerating ? "not-allowed" : "pointer",
                            padding: "6px 10px",
                            fontSize: "0.8rem",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px"
                        }}
                        title={isRegenerating ? "Regenerating..." : "Regenerate QR Code"}
                    >
                        <FontAwesomeIcon 
                            icon={faSync} 
                            spin={isRegenerating}
                            style={{ fontSize: "0.75rem", width: "0.75rem" }}
                        />
                        {isRegenerating ? "Regenerating..." : "Regenerate"}
                    </button>
                )}
                {/* Close button */}
                <button
                    onClick={onClose}
                    style={{
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                        background: "none",
                        border: "none",
                        fontSize: "1.5rem",
                        cursor: "pointer",
                        color: "#666",
                        padding: "5px",
                        lineHeight: 1,
                    }}
                    title="Close"
                >
                    ×
                </button>

                {/* Title */}
                {title && (
                    <h3 style={{
                        margin: "15px 0 1.5rem 0",
                        color: "#333",
                        fontSize: "1.2rem",
                        fontWeight: 600,
                    }}>
                        {title}
                    </h3>
                )}

                {/* QR Code Image */}
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "1.5rem",
                }}>
                    <img 
                        src={qrUrl} 
                        alt="QR Code"
                        style={{ 
                            maxWidth: "300px",
                            maxHeight: "300px",
                            width: "100%",
                            height: "auto",
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                        }}
                    />
                </div>

                {/* Instructions */}
                <p style={{
                    margin: "0 0 1rem 0",
                    color: "#666",
                    fontSize: "0.9rem",
                }}>
                    Point your camera at the QR code to scan
                </p>

                {/* Product/Pallet Information Display */}
                {(productId || productDescription || palletData || productData) && (
                    <div style={{
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #e9ecef",
                        borderRadius: "8px",
                        padding: "1rem",
                        margin: "1rem 0",
                        textAlign: "left",
                    }}>
                        <h4 style={{
                            margin: "0 0 0.75rem 0",
                            color: "#495057",
                            fontSize: "1rem",
                            fontWeight: 600,
                            textAlign: "center",
                            borderBottom: "1px solid #dee2e6",
                            paddingBottom: "0.5rem",
                        }}>
                            {palletData ? 'Pallet Information' : 'Product Information'}
                        </h4>
                        
                        {/* Pallet-specific information */}
                        {palletData && (
                            <div>
                                <div style={{ marginBottom: "0.5rem" }}>
                                    <strong style={{ color: "#495057", fontSize: "0.9rem" }}>Pallet Code:</strong>
                                    <span style={{ marginLeft: "0.5rem", color: "#6c757d", fontSize: "0.9rem" }}>
                                        {palletData.pallet_code}
                                    </span>
                                </div>
                                
                                <div style={{ marginBottom: "0.5rem" }}>
                                    <strong style={{ color: "#495057", fontSize: "0.9rem" }}>Location:</strong>
                                    <span style={{ marginLeft: "0.5rem", color: "#6c757d", fontSize: "0.9rem" }}>
                                        Lane {palletData.lane_id || 'N/A'}
                                    </span>
                                </div>
                                
                                <div style={{ marginBottom: "0.5rem" }}>
                                    <strong style={{ color: "#495057", fontSize: "0.9rem" }}>Type:</strong>
                                    <span style={{ marginLeft: "0.5rem", color: "#6c757d", fontSize: "0.9rem" }}>
                                        {palletData.pallet_type || 'N/A'}
                                    </span>
                                </div>
                                
                                {palletData.customer_name && (
                                    <div style={{ marginBottom: "0.5rem" }}>
                                        <strong style={{ color: "#495057", fontSize: "0.9rem" }}>Customer:</strong>
                                        <span style={{ marginLeft: "0.5rem", color: "#6c757d", fontSize: "0.9rem" }}>
                                            {palletData.customer_name}
                                        </span>
                                    </div>
                                )}
                                
                            </div>
                        )}
                        
                        {/* Product-specific information */}
                        {productData && (
                            <div>
                                <div style={{ marginBottom: "0.5rem" }}>
                                    <strong style={{ color: "#495057", fontSize: "0.9rem" }}>Product Code:</strong>
                                    <span style={{ marginLeft: "0.5rem", color: "#6c757d", fontSize: "0.9rem" }}>
                                        {productData.product_code}
                                    </span>
                                </div>
                                
                                <div style={{ marginBottom: "0.5rem" }}>
                                    <strong style={{ color: "#495057", fontSize: "0.9rem" }}>Product Name:</strong>
                                    <span style={{ marginLeft: "0.5rem", color: "#6c757d", fontSize: "0.9rem" }}>
                                        {productData.product_name}
                                    </span>
                                </div>
                                
                                {productData.batch_no && (
                                    <div style={{ marginBottom: "0.5rem" }}>
                                        <strong style={{ color: "#495057", fontSize: "0.9rem" }}>Batch No:</strong>
                                        <span style={{ marginLeft: "0.5rem", color: "#6c757d", fontSize: "0.9rem" }}>
                                            {productData.batch_no}
                                        </span>
                                    </div>
                                )}
                                
                                {productData.customer_name && (
                                    <div style={{ marginBottom: "0.5rem" }}>
                                        <strong style={{ color: "#495057", fontSize: "0.9rem" }}>Customer:</strong>
                                        <span style={{ marginLeft: "0.5rem", color: "#6c757d", fontSize: "0.9rem" }}>
                                            {productData.customer_name}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {/* Fallback for legacy data */}
                        {!palletData && !productData && (productId || productDescription) && (
                            <div>
                                {productId && (
                                    <div style={{ marginBottom: "0.5rem" }}>
                                        <strong style={{ color: "#495057", fontSize: "0.9rem" }}>
                                            {title?.includes('Pallet') ? 'Pallet Code:' : 'Product Code:'}
                                        </strong>
                                        <span style={{ marginLeft: "0.5rem", color: "#6c757d", fontSize: "0.9rem" }}>
                                            {productId}
                                        </span>
                                    </div>
                                )}
                                
                                {productDescription && (
                                    <div>
                                        <strong style={{ color: "#495057", fontSize: "0.9rem" }}>
                                            {title?.includes('Pallet') ? 'Description:' : 'Product Name:'}
                                        </strong>
                                        <span style={{ marginLeft: "0.5rem", color: "#6c757d", fontSize: "0.9rem" }}>
                                            {productDescription}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Download / Close Button */}
                <button
                    onClick={downloadable ? downloadPDF : onClose}
                    style={{
                        padding: "0.5rem 1.5rem",
                        backgroundColor: "#28a745",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "1rem",
                        fontWeight: 500,
                    }}
                >
                    {downloadable ? "Download PDF" : "Close"}
                </button>
            </div>
        </div>
    );
}

QRViewModal.propTypes = {
    qrUrl: PropTypes.string,
    title: PropTypes.string,
    productId: PropTypes.string,
    productDescription: PropTypes.string,
    palletData: PropTypes.object,  // Complete pallet object with items
    productData: PropTypes.object, // Complete product object
    onRegenerateQR: PropTypes.func, // Callback for QR regeneration
    onClose: PropTypes.func.isRequired,
};
